/* eslint-disable react-hooks/exhaustive-deps */
// Click-to-call and My desk for the whole app (CU-869fm5hj1, CU-869fmcc9r).
// One provider, mounted in app.jsx, owns the desk choice, the dial flow and
// its dialogs; Call buttons, the pill, the Call Logs desk chip and the user
// menu just ask it. The desk picker shows for anyone the backend's desk list
// answers (Call Logs users); Call buttons only when it says `can_dial`
// (B6 §3, contract ~/pm/contracts/B6.md v1).
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from '../../redux/store';
import { useAuth } from '../../hooks';
import useCustomSnackbar from '../../hooks/useCustomSnackbar';
import { isOutboundEnabled } from '../callPill/callPillFlag';
import { claimDesk, controlCall, dial, fetchDesks, releaseDesk } from './outboundApi';
import {
    RESULT, deskLabel, deskSummary, isDiallable, newIdempotencyKey, readDeskId, readLastDeskId, readResult, readStatus,
    rememberLastDesk, shouldAskDesk, suggestedDesk, writeDeskId,
} from './outboundModel';
import { registerBeforeLogout } from '../../utils/beforeLogout';
import { DeskClashDialog, DeskPickerDialog, HandsetFallbackDialog } from './OutboundDialogs';
import DeskPromptPill from './DeskPromptPill';

const OutboundContext = createContext(null);

// null outside the provider (tests, public pages): Call buttons render nothing.
export const useOutbound = () => useContext(OutboundContext);

const NO_RESPONSE = 'No answer from the server. Check your desk phone before trying again.';
const NO_RESPONSE_DESK = 'No answer from the server. Open the desk picker again to check which desk this computer is.';
// After a dial goes through, the same number can't be dialled again from this
// tab for this long (the desk phone is still ringing).
export const REDIAL_COOLDOWN_MS = 20 * 1000;
const EMPTY_STATUS = { canPickDesk: false, canCall: false, desks: [], myDeskId: null, defaultDeskId: null, deskPrompt: true };

// The sign-in desk prompt shows at most once per user, practice and UK day (CU-869fmd1vf).
const UK_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' });
export const deskPromptKey = (userId, practiceId) => `outbound:deskPrompt:${userId}:${practiceId}`;
export const takeDeskPrompt = (userId, practiceId, now = new Date()) => {
    try {
        const today = UK_DAY.format(now);
        const key = deskPromptKey(userId, practiceId);
        if (window.localStorage.getItem(key) === today) return false;
        window.localStorage.setItem(key, today);
        return true;
    } catch {
        return false; // no storage: never nag on every page load
    }
};

export default function OutboundCallProvider({ children }) {
    const { isAuthenticated, user } = useAuth();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const userId = isAuthenticated ? user?.pk : null;
    const { enqueueSnackbar } = useCustomSnackbar();
    const [status, setStatus] = useState(EMPTY_STATUS);
    const [deskId, setDeskId] = useState(() => readDeskId(practiceId));
    const [dialog, setDialog] = useState(null);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const controlBusyRef = useRef(new Set());
    const lastDialRef = useRef(null);
    const scope = userId && practiceId ? `${userId}:${practiceId}` : null;
    const scopeRef = useRef(scope);
    scopeRef.current = scope;

    const loadStatus = useCallback(async () => {
        if (!scope) { setStatus(EMPTY_STATUS); return null; }
        const requestScope = scope;
        const res = await fetchDesks(practiceId);
        if (scopeRef.current !== requestScope) return null;
        const read = readStatus(res);
        // The click-to-call kill switch hides Call buttons, never the desk picker.
        const next = { ...read, canCall: read.canCall && isOutboundEnabled() };
        setStatus(next);
        // The desk the server says I hold is the truth (another tab or computer may have changed it).
        if (next.myDeskId) {
            writeDeskId(practiceId, next.myDeskId);
            setDeskId(next.myDeskId);
        }
        return next;
    }, [scope, practiceId]);

    // A practice switch drops the old practice's permission and desks at once,
    // before the new status arrives.
    useEffect(() => {
        setStatus(EMPTY_STATUS);
        setDeskId(readDeskId(practiceId));
        setDialog(null);
        lastDialRef.current = null;
        const requestScope = scope;
        loadStatus().then((next) => {
            // Sign-in prompt (CU-869fmd1vf): once a day per practice, as a pill that never blocks the
            // page (CU-869fmdxu8). Only for someone who can press Call, holds no desk and shares no
            // handset, at a practice that hasn't switched it off (CU-869fncprw); everyone else is
            // asked when they press Call. Checked before the day is used up. Not today / × hide it
            // until tomorrow.
            if (scopeRef.current !== requestScope || !shouldAskDesk(next)) return;
            if (!takeDeskPrompt(userId, practiceId)) return;
            setDialog((current) => current || { type: 'signin' });
        });
    }, [loadStatus, practiceId]);

    // A desk claimed elsewhere (another tab, an admin's Assign) answers the sign-in prompt.
    useEffect(() => {
        if (status.myDeskId) setDialog((current) => (current?.type === 'signin' ? null : current));
    }, [status.myDeskId]);

    // Back on this tab: re-read the desks, so a take-over (or a 12 h lapse) shows on My desk.
    useEffect(() => {
        if (!scope) return undefined;
        const onVisible = () => { if (!document.hidden) loadStatus(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [scope, loadStatus]);

    // Signing out ends the claim: calls to the desk stop ringing for this user. Every practice
    // where this session held a desk is released, not only the one open now.
    const heldPracticesRef = useRef(new Set());
    useEffect(() => {
        if (!practiceId) return;
        if (status.myDeskId) heldPracticesRef.current.add(String(practiceId));
        else if (status.canPickDesk) heldPracticesRef.current.delete(String(practiceId));
    }, [practiceId, status.myDeskId, status.canPickDesk]);
    useEffect(() => {
        if (!userId) return undefined;
        const held = heldPracticesRef.current;
        return registerBeforeLogout(async () => {
            const practices = [...held];
            held.clear();
            await Promise.allSettled(practices.map((p) => {
                writeDeskId(p, null);
                return releaseDesk(p, { reason: 'logout' });
            }));
        });
    }, [userId]);

    const desk = useMemo(() => status.desks.find((d) => String(d.id) === String(deskId)) || null, [status.desks, deskId]);
    // The desk this user holds right now, per the server: what My desk shows and the pill names.
    const myDesk = useMemo(() => status.desks.find((d) => String(d.id) === String(status.myDeskId)) || null, [status.desks, status.myDeskId]);

    // Guards a non-idempotent write against double clicks.
    const guarded = async (fn) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        try { await fn(); } finally { busyRef.current = false; setBusy(false); }
    };

    // True when the user or practice changed while a request was in flight:
    // its answer belongs to the old context and is dropped.
    const stale = (requestScope) => scopeRef.current !== requestScope;

    const placeCall = async (request, useDesk) => {
        const requestScope = scope;
        // B6 dials from the desk this user holds (claimed just before).
        const result = readResult(await dial(practiceId, {
            to: request.to, leadId: request.leadId, idempotencyKey: newIdempotencyKey(),
        }));
        if (stale(requestScope)) return;
        switch (result.kind) {
        case RESULT.OK:
            // 202 "dialling": the desk rings first. "Connected" comes from the
            // call events (pill), never from this response.
            lastDialRef.current = { to: request.to, at: Date.now() };
            setDialog(null);
            enqueueSnackbar(`Ringing ${deskLabel(useDesk)} — pick up the desk phone to connect.`, { variant: 'info' });
            break;
        case RESULT.IN_USE:
            setDialog({ type: 'clash', desk: useDesk, heldBy: result.heldBy, request });
            break;
        case RESULT.MANUAL:
            setDialog({ type: 'fallback', desk: useDesk, number: result.number || request.to, reason: result.reason });
            break;
        case RESULT.UNKNOWN:
            // It may have dialled: treat it like a placed call for the cooldown.
            lastDialRef.current = { to: request.to, at: Date.now() };
            setDialog(null);
            enqueueSnackbar(NO_RESPONSE, { variant: 'warning' });
            break;
        default:
            setDialog(null);
            enqueueSnackbar(result.message);
        }
    };

    const claimThenCall = async (chosen, request, { takeOver = false, fromPrompt = false } = {}) => {
        const requestScope = scope;
        const claimPractice = practiceId;
        const result = readResult(await claimDesk(claimPractice, chosen.id, { takeOver }));
        if (stale(requestScope)) return;
        if (result.kind === RESULT.IN_USE) { setDialog({ type: 'clash', desk: chosen, heldBy: result.heldBy, request, fromPrompt }); return; }
        if (result.kind === RESULT.UNKNOWN) { setDialog(null); enqueueSnackbar(NO_RESPONSE_DESK, { variant: 'warning' }); return; }
        if (result.kind !== RESULT.OK) { setDialog(null); enqueueSnackbar(result.message); return; }
        // A claim releases any other desk this user held (B6 §3).
        writeDeskId(claimPractice, chosen.id);
        rememberLastDesk(claimPractice, chosen.id);
        setDeskId(String(chosen.id));
        setStatus((s) => ({ ...s, myDeskId: String(chosen.id) }));
        if (request) await placeCall(request, chosen);
        else {
            setDialog(null);
            // From the sign-in pill it collapses into My desk in the header: a short confirmation.
            enqueueSnackbar(
                fromPrompt ? `Desk set: ${deskLabel(chosen)}` : `You're at ${deskSummary(chosen)}. Its calls will pop up here.`,
                { variant: 'success' },
            );
            loadStatus();
        }
    };

    const releaseMyDesk = useCallback(() => guarded(async () => {
        const requestScope = scope;
        const forPractice = practiceId;
        const result = readResult(await releaseDesk(forPractice));
        if (stale(requestScope)) return;
        if (result.kind === RESULT.UNKNOWN) { enqueueSnackbar(NO_RESPONSE_DESK, { variant: 'warning' }); return; }
        if (result.kind !== RESULT.OK) { enqueueSnackbar(result.message); return; }
        writeDeskId(forPractice, null);
        setDeskId(null);
        setStatus((s) => ({ ...s, myDeskId: null }));
        setDialog(null);
        enqueueSnackbar('Desk released. Its calls won\'t pop up for you any more.', { variant: 'info' });
        loadStatus();
    }), [scope, practiceId, loadStatus]);

    const openDeskPicker = useCallback((request = null) => {
        setDialog({ type: 'picker', request });
        // Fresh presence ("in use by …") for the list.
        loadStatus();
    }, [loadStatus]);

    const startCall = useCallback((request) => {
        if (!status.canCall || !isDiallable(request?.to)) return;
        if (busyRef.current) {
            enqueueSnackbar('A call is already being placed.', { variant: 'info' });
            return;
        }
        const last = lastDialRef.current;
        if (last && last.to === request.to && Date.now() - last.at < REDIAL_COOLDOWN_MS) {
            enqueueSnackbar(`Already calling ${request.to} — pick up your desk phone.`, { variant: 'info' });
            return;
        }
        // The server's claim decides which desk rings, not what this tab remembers: an admin
        // may have assigned this user elsewhere or released the desk, or it lapsed (CU-869fmd1vf).
        // No desk held → the picker, with the remembered desk pre-selected.
        loadStatus().then((fresh) => {
            if (!fresh?.canCall) return;
            const held = fresh.desks.find((d) => String(d.id) === String(fresh.myDeskId));
            if (!held) { openDeskPicker(request); return; }
            // Re-claim first: it's the presence heartbeat and surfaces a take-over by
            // someone else before dialling.
            guarded(() => claimThenCall(held, request));
        });
    }, [status.canCall, loadStatus, openDeskPicker, practiceId]);

    // Transfer / End from the pill; one action per call at a time.
    const control = useCallback(async (call, action, body = {}) => {
        if (controlBusyRef.current.has(call.callId)) return;
        controlBusyRef.current.add(call.callId);
        const requestScope = scope;
        try {
            const result = readResult(await controlCall(practiceId, call.callId, action, body));
            if (stale(requestScope)) return;
            if (result.kind === RESULT.UNKNOWN) enqueueSnackbar(NO_RESPONSE, { variant: 'warning' });
            // The handset can't act (not set up / sign-in expired / none shared): the call is
            // fine, it just has to be moved on the desk phone (CU-869fn0jee).
            else if (result.kind === RESULT.MANUAL) enqueueSnackbar(result.message, { variant: 'warning' });
            else if (result.kind !== RESULT.OK) enqueueSnackbar(result.message);
        } finally {
            controlBusyRef.current.delete(call.callId);
        }
    }, [practiceId, scope]);

    // "Call back" on a missed-call pill opens the one dialer pre-filled
    // (TwilioVoiceProvider, CU-869fmbgfj); Call there comes back to startCall.

    const value = useMemo(() => ({
        canCall: status.canCall,
        canPickDesk: status.canPickDesk,
        desks: status.desks,
        deskId,
        desk,
        myDesk,
        busy,
        startCall,
        openDeskPicker,
        releaseMyDesk,
        control,
    }), [status.canCall, status.canPickDesk, status.desks, deskId, desk, myDesk, busy, startCall, openDeskPicker, releaseMyDesk, control]);

    const close = () => setDialog(null);
    // Back to the sign-in pill from its clash dialog (Cancel, or Pick another with the list open).
    const backToPrompt = (startOpen) => setDialog({ type: 'signin', startOpen });
    // Usual desk, else the last desk this browser claimed (kept through sign-out).
    const prompting = dialog?.type === 'signin';
    const lastDeskId = useMemo(() => (prompting ? readLastDeskId(practiceId) : null), [prompting, practiceId]);
    const suggested = prompting ? suggestedDesk(status.desks, { usualDeskId: status.defaultDeskId, lastDeskId: lastDeskId || deskId }) : null;

    return (
        <OutboundContext.Provider value={value}>
            {children}
            {dialog?.type === 'signin' && (
                <DeskPromptPill
                    desks={status.desks}
                    suggested={suggested}
                    heldDeskId={status.myDeskId}
                    usualDeskId={status.defaultDeskId}
                    showCallSetup={status.canCall}
                    busy={busy}
                    startOpen={Boolean(dialog.startOpen)}
                    onDismiss={close}
                    onPick={(d) => guarded(() => claimThenCall(d, null, { fromPrompt: true }))}
                />
            )}
            {dialog?.type === 'picker' && (
                <DeskPickerDialog
                    open
                    desks={status.desks}
                    // This browser's desk, else the handset last used here (kept through sign-out).
                    currentDeskId={status.myDeskId || deskId || readLastDeskId(practiceId)}
                    lastDeskId={readLastDeskId(practiceId)}
                    heldDeskId={status.myDeskId}
                    usualDeskId={status.defaultDeskId}
                    showCallSetup={status.canCall}
                    onRelease={releaseMyDesk}
                    onClose={close}
                    onPick={(d) => guarded(() => claimThenCall(d, dialog.request))}
                />
            )}
            {dialog?.type === 'clash' && (
                <DeskClashDialog
                    open
                    desk={dialog.desk}
                    heldBy={dialog.heldBy}
                    onClose={dialog.fromPrompt ? () => backToPrompt(false) : close}
                    onTakeOver={() => guarded(() => claimThenCall(dialog.desk, dialog.request, { takeOver: true, fromPrompt: dialog.fromPrompt }))}
                    onPickAnother={dialog.fromPrompt ? () => backToPrompt(true) : () => openDeskPicker(dialog.request)}
                />
            )}
            {dialog?.type === 'fallback' && (
                <HandsetFallbackDialog open number={dialog.number} desk={dialog.desk} reason={dialog.reason} onClose={close} />
            )}
        </OutboundContext.Provider>
    );
}

OutboundCallProvider.propTypes = { children: PropTypes.node };
